'use client';

import {useSuspenseQuery} from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import {useRouter} from 'next/navigation';
import {useTranslations} from 'next-intl';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ExperienceCardShell, NoResults} from '@/entities/Experiences';
import {experiencesListOptions} from '@/entities/Experiences/api/experiences';
import {Button} from '@/shared/components/Button';

interface TrendingAuctionsSectionProps {
  celebrityId?: number;
  title?: React.ReactNode;
}

const TrendingAuctionsSection: React.FC<TrendingAuctionsSectionProps> = React.memo(({celebrityId, title}) => {
  const t = useTranslations('TrendingAuctions');
  const router = useRouter();

  const {
    data: {items: experiences},
  } = useSuspenseQuery(
    experiencesListOptions({
      celebrityId: typeof celebrityId === 'number' ? [celebrityId] : undefined,
      limit: 4,
    }),
  );

  const experiencesUrl = useMemo(
    () => (celebrityId ? `/experiences?celebrities=${celebrityId}` : '/experiences'),
    [celebrityId],
  );

  // Embla Carousel для мобильных устройств
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    skipSnaps: false,
    containScroll: 'trimSnaps',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  if (celebrityId && experiences.length === 0) {
    return;
  }

  return (
    <section className={`w-full bg-[#131424] px-0 py-[64px] md:px-4`}>
      {/* Контейнер */}
      <div className="container mx-auto max-w-[1440px] px-[20px]">
        {/* Заголовок */}
        <div className="mb-6 text-center lg:mb-[32px]">
          <h2 className="font-secondary text-[32px] font-bold leading-[1.1] md:text-[60px] lg:text-[90px]">
            {title ? (
              <div className="whitespace-pre-wrap font-extrabold uppercase italic leading-[0.9] text-transparent text-white lg:text-[64px]">
                {title}
              </div>
            ) : (
              <>
                <div className="font-secondary text-[18px] font-bold uppercase text-white md:text-[32px] lg:text-[42px]">
                  {t('Subtitle')}
                </div>
                <div className="bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text uppercase text-transparent">
                  {t('Title')}
                </div>
              </>
            )}
          </h2>
        </div>

        {experiences.length ? (
          <div className="-mx-5 mb-5 lg:mb-[30px]">
            {/* Мобильная карусель */}
            <div className="block md:hidden">
              <div className="relative">
                <div className="overflow-hidden" ref={emblaRef}>
                  <div className={`flex ${experiences.length === 1 ? ' justify-center' : ''}`}>
                    {experiences.map((experience, index) => (
                      <div
                        key={experience.id}
                        className={
                          'flex min-w-0 flex-[0_0_calc((248/3.2)*1%)] flex-col justify-center px-1' +
                          (index !== selectedIndex ? ' opacity-30' : '')
                        }
                      >
                        <ExperienceCardShell
                          key={experience.id}
                          experience={experience}
                          isLCP={index === 0}
                          countdownTimer={true}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Десктопная сетка */}
            <div className="hidden md:block">
              <div className="mx-auto grid max-w-[1192px]  grid-cols-2 gap-5 tablet:grid-cols-3 lg:grid-cols-4">
                {experiences.map((experience, index) => (
                  <ExperienceCardShell
                    key={experience.id}
                    experience={experience}
                    isLCP={index === 0}
                    countdownTimer={true}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <NoResults />
        )}
        {experiences.length && (
          <div className="flex justify-center">
            <div className="flex w-48 flex-col items-center gap-[9px]">
              <Button
                variant="whiteSecondaryOutline"
                size="2md"
                onClick={() => router.push(experiencesUrl)}
                className="hidden w-full max-w-[350px] md:inline-flex"
              >
                {t('Button.ViewAll')}
              </Button>
              <Button
                variant="whiteSecondaryOutline"
                size="md"
                onClick={() => router.push(experiencesUrl)}
                className="w-full max-w-[288px] md:hidden"
              >
                {t('Button.ViewAll')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

TrendingAuctionsSection.displayName = 'TrendingAuctionsSection';

export default TrendingAuctionsSection;
